import AwsS3Multipart from "@uppy/aws-s3-multipart"
import Uppy, { type FailedUppyFile, type State } from "@uppy/core"
import ThumbnailGenerator from "@uppy/thumbnail-generator"
import XHRUpload from "@uppy/xhr-upload"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import env from "@/common/env"
import { formatBytes } from "@/common/util/misc"
import "@uppy/core/dist/style.min.css"
import "@uppy/drag-drop/dist/style.min.css"
import "./use_uppy.css"

const maxFileSize = 25 * 1024 * 1024

// We use this to restore the file list if we unmount an uploader and remount it later again (toolbox tabs etc).
// Keyed by uploader ID.
const uppyFileMap: Record<string, State["files"]> = {}

const createUppy = (id: string, allowedFileTypes?: string[], thumbnails?: boolean, maxNumberOfFiles?: number) => {
  const uppy = new Uppy({
    id,
    autoProceed: true,
    restrictions: {
      maxFileSize,
      allowedFileTypes,
      maxNumberOfFiles,
    },
  })

  if (env.TEST) {
    uppy.use(XHRUpload, {
      endpoint: "/test-upload",
      responseUrlFieldName: "uploadURL",
    })
  } else {
    uppy.use(AwsS3Multipart, {
      limit: 4,
      companionUrl: "/",
    })
  }

  if (thumbnails) {
    uppy.use(ThumbnailGenerator, {
      thumbnailHeight: 200,
      thumbnailType: "image/png",
      waitForThumbnailsBeforeUpload: true,
    })
  }

  if (uppyFileMap[id]) {
    uppy.setState({
      files: uppyFileMap[id],
    })
  }

  return uppy
}

export interface UppyRestrictionError extends Error {
  isRestriction: boolean
  message: string
  stack: string
}

export type InvalidUppyFile = FailedUppyFile<any, any>

export interface UseUppyReturn {
  uppy: Uppy
  state: State
  invalidFiles: InvalidUppyFile[]
  removeInvalidFile: (file: InvalidUppyFile) => void
  clearInvalidFiles: () => void
}

export const useUppy = (
  id: string,
  allowedFileTypes?: string[],
  thumbnails?: boolean,
  maxNumberOfFiles?: number
): UseUppyReturn => {
  const { t } = useTranslation()
  const uppy = useMemo(
    () => createUppy(id, allowedFileTypes, thumbnails, maxNumberOfFiles),
    [id, allowedFileTypes, thumbnails, maxNumberOfFiles]
  )
  const [uppyState, setUppyState] = useState(uppy.getState())
  const [invalidFiles, setInvalidFiles] = useState<InvalidUppyFile[]>([])

  useEffect(() => {
    const updateState = () => {
      const state = uppy.getState()
      uppyFileMap[id] = state.files
      setUppyState(state)
    }

    uppy.on("file-added", updateState)
    uppy.on("file-removed", updateState)
    uppy.on("upload", updateState)
    uppy.on("progress", updateState)
    uppy.on("error", updateState)
    uppy.on("complete", updateState)
    uppy.on("upload-retry", updateState)
    uppy.on("cancel-all", updateState)
    uppy.on("restriction-failed", updateState)
    uppy.on("reset-progress", updateState)

    uppy.on("restriction-failed", (file: FailedUppyFile<any, any>, error: UppyRestrictionError) => {
      if (error.isRestriction) {
        if (file.size > maxFileSize) {
          file.error = t("uploader.size_error", { maxSize: formatBytes(maxFileSize) })
        } else if (error.message.includes("You can only upload")) {
          file.error = t("errors.image_extension")
        } else {
          file.error = error.message
        }

        setInvalidFiles(files => [...files, file])
      }
    })

    return () => {
      uppy.off("file-added", updateState)
      uppy.off("file-removed", updateState)
      uppy.off("upload", updateState)
      uppy.off("progress", updateState)
      uppy.off("error", updateState)
      uppy.off("complete", updateState)
      uppy.off("upload-retry", updateState)
      uppy.off("cancel-all", updateState)
      uppy.off("restriction-failed", updateState)
      uppy.off("reset-progress", updateState)
    }
  }, [t, uppy, id])

  const removeInvalidFile = useCallback((file: InvalidUppyFile) => {
    setInvalidFiles(files => files.filter(f => f !== file))
  }, [])

  const clearInvalidFiles = useCallback(() => {
    setInvalidFiles([])
  }, [])

  return { uppy, state: uppyState, invalidFiles, removeInvalidFile, clearInvalidFiles }
}
